import React, { useMemo, useRef, useState } from "react";
import {
  Trash2, Loader2, Upload, Download, FileText, FileSpreadsheet,
  FileImage, FileAudio, FileVideo, File,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button, IconButton } from "../../atoms";
import {
  useLeadDocuments, useUploadDocument, useDeleteDocument, fetchDocumentDownloadUrl,
} from "../../hooks/useLeads";
import { useTeamMembers, formatMember } from "../../hooks/useTeamMembers";
import useFocusRow, { FOCUS_ROW_STYLE } from "../../hooks/useFocusRow";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";

function formatRelative(d) {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(d).toLocaleDateString();
}

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mimeType) {
  const m = String(mimeType || "");
  if (m.startsWith("image/")) return FileImage;
  if (m.startsWith("audio/")) return FileAudio;
  if (m.startsWith("video/")) return FileVideo;
  if (m.includes("spreadsheet") || m.includes("excel") || m === "text/csv") return FileSpreadsheet;
  if (m.includes("pdf") || m.includes("word") || m.includes("text/")) return FileText;
  return File;
}

function DocumentRow({
  doc, rowRef, selected, onToggleSelect, onDeleteRequest, onDownload, downloading, uploaderName,
}) {
  const Icon = fileIcon(doc.mimeType);

  return (
    <div
      ref={rowRef}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 8,
        background: selected ? "var(--crm-primary-soft)" : "var(--color-bg-surface)",
        border: `1px solid ${selected ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
        transition: "background 120ms ease, border-color 120ms ease",
      }}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggleSelect(doc._id)}
        aria-label={`Select ${doc.filename}`}
        style={{ cursor: "pointer", width: 15, height: 15, flexShrink: 0 }}
      />
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {doc.filename}
        </div>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
          {fmtBytes(doc.sizeBytes)} · {uploaderName} · {formatRelative(doc.createdAt)}
        </div>
        {doc.description && (
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4, lineHeight: "18px" }}>
            {doc.description}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        <IconButton
          aria-label="Download file"
          onClick={() => onDownload(doc)}
          disabled={downloading === doc._id}
        >
          {downloading === doc._id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        </IconButton>
        <IconButton aria-label="Delete file" onClick={() => onDeleteRequest(doc)}>
          <Trash2 size={14} />
        </IconButton>
      </div>
    </div>
  );
}

export default function FilesPanel({ leadId, focusItemId, onFocusResolved }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [confirmDoc, setConfirmDoc] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const { data, isLoading } = useLeadDocuments(leadId);
  const upload = useUploadDocument(leadId);
  const del = useDeleteDocument(leadId);
  const { register } = useFocusRow(focusItemId, { onResolved: onFocusResolved });
  const { members } = useTeamMembers();
  const memberByEmail = useMemo(() => {
    const map = new Map();
    members.forEach((m) => { if (m.email) map.set(m.email, m); });
    return map;
  }, [members]);

  const documents = data?.documents || [];
  const docIds = useMemo(() => documents.map((d) => d._id), [documents]);

  const allSelected = documents.length > 0 && docIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(docIds));
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    for (const file of files) {
      try {
        await upload.mutateAsync(file);
        toast.success(`Uploaded ${file.name}`);
      } catch (err) {
        toast.error(err?.response?.data?.error || err.message || `Failed to upload ${file.name}`);
      }
    }
  };

  const onFileInput = (e) => {
    uploadFiles(e.target.files);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const downloadDoc = async (doc) => {
    setDownloading(doc._id);
    try {
      const { url, filename } = await fetchDocumentDownloadUrl(leadId, doc._id);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || doc.filename;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const performDelete = async () => {
    if (!confirmDoc) return;
    try {
      await del.mutateAsync(confirmDoc._id);
      setSelected((prev) => {
        if (!prev.has(confirmDoc._id)) return prev;
        const next = new Set(prev);
        next.delete(confirmDoc._id);
        return next;
      });
      toast.success("File deleted");
      setConfirmDoc(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Delete failed");
    }
  };

  const performBulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkDeleting(true);
    let deleted = 0;
    let failed = 0;
    try {
      for (const id of ids) {
        try {
          await del.mutateAsync(id);
          deleted += 1;
        } catch {
          failed += 1;
        }
      }
      setSelected(new Set());
      setConfirmBulk(false);
      if (deleted) toast.success(`Deleted ${deleted} file${deleted === 1 ? "" : "s"}`);
      if (failed) toast.error(`${failed} file${failed === 1 ? "" : "s"} could not be deleted`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const selectedFilenames = useMemo(
    () => documents.filter((d) => selected.has(d._id)).map((d) => d.filename),
    [documents, selected],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <style>{FOCUS_ROW_STYLE}</style>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `1.5px dashed ${dragOver ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
          borderRadius: 8,
          padding: "28px 20px",
          textAlign: "center",
          cursor: upload.isPending ? "wait" : "pointer",
          background: dragOver ? "var(--crm-primary-soft)" : "var(--color-bg-muted)",
          transition: "border-color 120ms ease, background 120ms ease",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={onFileInput}
          style={{ display: "none" }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp,.gif,.mp3,.mp4,.wav,.webm"
        />
        <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--color-text-secondary)" }}>
          {upload.isPending ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
            {upload.isPending ? "Uploading…" : "Drop files here or click to upload"}
          </span>
        </div>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 6 }}>
          PDF, Office docs, images, audio, video · max 25MB each
        </div>
      </div>

      {isLoading && (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>Loading files…</div>
      )}

      {!isLoading && documents.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", textAlign: "center", padding: "12px 0", fontStyle: "italic" }}>
          No files attached yet
        </div>
      )}

      {!isLoading && documents.length > 0 && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, flexWrap: "wrap",
          padding: "8px 10px",
          borderRadius: 8,
          background: "var(--color-bg-muted)",
          border: "1px solid var(--color-border-subtle)",
        }}>
          <label style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)", cursor: "pointer",
          }}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              aria-label="Select all files"
              style={{ cursor: "pointer", width: 15, height: 15 }}
            />
            Select all
          </label>
          {someSelected && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                {selected.size} selected
              </span>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setConfirmBulk(true)}
                disabled={bulkDeleting}
                leftIcon={bulkDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              >
                Delete selected
              </Button>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {documents.map((doc) => {
          const member = doc.uploadedByEmail ? memberByEmail.get(doc.uploadedByEmail) : null;
          const uploaderName = member ? formatMember(member) : (doc.uploadedByEmail || "unknown");
          return (
            <DocumentRow
              key={doc._id}
              doc={doc}
              rowRef={register(doc._id)}
              selected={selected.has(doc._id)}
              onToggleSelect={toggleOne}
              onDeleteRequest={setConfirmDoc}
              onDownload={downloadDoc}
              downloading={downloading}
              uploaderName={uploaderName}
            />
          );
        })}
      </div>

      <ConfirmDeleteModal
        open={!!confirmDoc}
        onClose={() => setConfirmDoc(null)}
        onConfirm={performDelete}
        title="Delete file?"
        message={confirmDoc ? `"${confirmDoc.filename}" will be removed.` : ""}
        confirmLabel="Delete file"
        isPending={del.isPending}
      />

      <ConfirmDeleteModal
        open={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        onConfirm={performBulkDelete}
        title={`Delete ${selected.size} file${selected.size === 1 ? "" : "s"}?`}
        message="These files will be permanently removed from this lead."
        detail={selectedFilenames.length <= 4
          ? selectedFilenames.join(", ")
          : `${selectedFilenames.slice(0, 4).join(", ")}, and ${selectedFilenames.length - 4} more`}
        confirmLabel={`Delete ${selected.size} file${selected.size === 1 ? "" : "s"}`}
        isPending={bulkDeleting}
      />
    </div>
  );
}
